/* ============================================
   LeadOverviewTab — de qué se trata la oportunidad

   Tres bloques y la actividad: Detalles (cómo llegó),
   Comercial (cuánto vale y para cuándo) y lo que dejó
   el formulario. Todo lo editable se edita en su sitio,
   sin modal: son campos que se corrigen a mitad de una
   llamada.
   ============================================ */
import { useState } from 'react'
import { useApi } from '@/hooks/useApi'
import type { Lead, FormularioItem, PrioridadProspecto, Servicio } from '@/lib/types'
import OriginBadge from '@/components/shared/OriginBadge'
import LeadLienzo from './LeadLienzo'
import { IconEdit, IconExternalLink, IconGlobe } from '@/components/icons/Icons'

interface LeadOverviewTabProps {
  lead: Lead
  /** Catálogo para el selector de servicio; lo trae la ficha una sola vez. */
  servicios: Servicio[]
  onRefresh: () => void
}

const PRIORIDADES: PrioridadProspecto[] = ['A', 'B', 'C']

/** El enlace se muestra sin protocolo ni barra final: la URL entera no entra. */
function urlCorta(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '')
}

function fechaCorta(iso: string | null): string {
  if (!iso) return '—'
  const d = iso.length <= 10 ? new Date(iso + 'T12:00:00') : new Date(iso)
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function LeadOverviewTab({ lead, servicios, onRefresh }: LeadOverviewTabProps) {
  const api = useApi()
  const [editando, setEditando] = useState<string | null>(null)
  const [borrador, setBorrador] = useState('')
  const [senalDraft, setSenalDraft] = useState(lead.senal_dolor || '')

  const formData: FormularioItem[] = Array.isArray(lead.formulario_datos) ? lead.formulario_datos : []

  async function guardar(campo: string, valor: string | number | null, extra?: Record<string, unknown>) {
    setEditando(null)
    try {
      await api.put(`/api/leads/${lead.id}`, { [campo]: valor, ...extra })
      onRefresh()
    } catch (err: any) {
      console.error(`Error guardando ${campo}:`, err)
      alert(err?.message || `No se pudo guardar ${campo}`)
    }
  }

  function abrir(campo: string, valorActual: string) {
    setEditando(campo)
    setBorrador(valorActual)
  }

  /** Campo de texto/número/fecha que se edita en su propio sitio. */
  function Campo({ campo, label, valor, tipo = 'text', mostrado, placeholder, sufijo }: {
    campo: string
    label: string
    valor: string
    tipo?: 'text' | 'number' | 'date'
    mostrado: React.ReactNode
    placeholder?: string
    sufijo?: string
  }) {
    if (editando === campo) {
      return (
        <div className="ld-dato">
          <span className="ld-dato__label">{label}</span>
          <input
            type={tipo}
            className="form-input form-input--inline-mini"
            value={borrador}
            placeholder={placeholder}
            autoFocus
            onChange={e => setBorrador(e.target.value)}
            onBlur={() => guardar(campo, borrador.trim() === '' ? null : (tipo === 'number' ? Number(borrador) : borrador.trim()))}
            onKeyDown={e => {
              if (e.key === 'Enter') e.currentTarget.blur()
              if (e.key === 'Escape') setEditando(null)
            }}
          />
        </div>
      )
    }
    return (
      <button type="button" className="ld-dato ld-dato--editable" onClick={() => abrir(campo, valor)}>
        <span className="ld-dato__label">{label}</span>
        <span className="ld-dato__valor">
          {mostrado}{sufijo}
          <IconEdit size={11} />
        </span>
      </button>
    )
  }


  // El bloque solo aparece si el lead sigue en prospección o si ya trae datos
  // de ella: una oportunidad que llegó por formulario no tiene por qué verlo.
  // El texto libre solo cuenta como dato de prospección mientras no haya
  // servicio del catálogo: con `servicio_id` asignado es ruido viejo.
  const servicioLibre = !lead.servicio_id && !!lead.servicio_interes

  const esProspeccion = lead.etapa === 'prospecto'
    || !!(lead.ciudad || lead.senal_dolor || servicioLibre || lead.prioridad
      || lead.linkedin_url || lead.web_url)

  return (
    <div className="ld-overview">
      {/* ─── Lienzo ───
          Antes de Detalles a propósito: lo primero que uno debería leer al
          abrir una oportunidad es lo que alguien escribió sobre ella, no una
          grilla de campos medio vacíos. */}
      <LeadLienzo lead={lead} onRefresh={onRefresh} />

      {/* ─── Detalles ─── */}
      <section className="ld-bloque">
        <h4 className="ld-bloque__titulo">Detalles</h4>
        <div className="ld-datos">
          <div className="ld-dato">
            <span className="ld-dato__label">Código</span>
            <span className="ld-dato__valor">{lead.codigo}</span>
          </div>
          <div className="ld-dato">
            <span className="ld-dato__label">Origen</span>
            <span className="ld-dato__valor"><OriginBadge origen={lead.origen} /></span>
          </div>
          <Campo
            campo="industria"
            label="Industria"
            valor={lead.industria || ''}
            placeholder="Retail, salud, educación..."
            mostrado={lead.industria || <span className="text-muted-italic">Sin definir</span>}
          />
          {/* El servicio vive acá y no en Prospección: ese bloque solo aparece
              con datos de outbound, y una oportunidad que entró por formulario
              también necesita su servicio — de él sale el material comercial. */}
          <div className="ld-dato">
            <span className="ld-dato__label">Servicio</span>
            <select
              className="form-select ld-dato__servicio"
              value={lead.servicio_id ?? ''}
              onChange={e => guardar('servicio_id', e.target.value === '' ? null : Number(e.target.value))}
              aria-label="Servicio del catálogo"
            >
              <option value="">— Sin servicio —</option>
              {servicios.map(s => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          </div>

          <div className="ld-dato">
            <span className="ld-dato__label">Primer contacto</span>
            <span className="ld-dato__valor">{fechaCorta(lead.fecha_primer_contacto)}</span>
          </div>
          {lead.formulario_id && (
            <div className="ld-dato">
              <span className="ld-dato__label">Formulario</span>
              <span className="ld-dato__valor">{lead.formulario_id}</span>
            </div>
          )}
        </div>
      </section>

      {/* ─── Prospección ─── */}
      {esProspeccion && (
        <section className="ld-bloque">
          <h4 className="ld-bloque__titulo">Prospección</h4>

          <div className="ld-datos">
            <Campo
              campo="ciudad"
              label="Ciudad"
              valor={lead.ciudad || ''}
              placeholder="Lima, CABA..."
              mostrado={lead.ciudad || <span className="text-muted-italic">Sin definir</span>}
            />

            <div className="ld-dato">
              <span className="ld-dato__label">Prioridad</span>
              <div className="ld-prioridad-chips">
                {PRIORIDADES.map(p => (
                  <button
                    key={p}
                    type="button"
                    className={`prio-chip prio-chip--${p.toLowerCase()}${lead.prioridad === p ? '' : ' prio-chip--off'}`}
                    onClick={() => guardar('prioridad', lead.prioridad === p ? null : p)}
                    title={p === 'A' ? 'Alta afinidad' : p === 'B' ? 'Requiere discovery' : 'Baja'}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Lo que escribió el SDR en la planilla. Se muestra solo mientras no
                haya servicio del catálogo: es el respaldo para lo que el lead
                pide y todavía no existe como servicio. */}
            {servicioLibre && (
              <Campo
                campo="servicio_interes"
                label="Servicio a ofrecer (texto libre)"
                valor={lead.servicio_interes || ''}
                placeholder="ERP de lotes, CRM omnicanal..."
                mostrado={lead.servicio_interes}
              />
            )}

            <Campo
              campo="linkedin_url"
              label="LinkedIn de la empresa"
              valor={lead.linkedin_url || ''}
              placeholder="https://linkedin.com/company/..."
              mostrado={lead.linkedin_url
                ? <a className="ld-enlace" href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                    {urlCorta(lead.linkedin_url)} <IconExternalLink size={10} />
                  </a>
                : <span className="text-muted-italic">Sin enlace</span>}
            />

            <Campo
              campo="web_url"
              label="Sitio web"
              valor={lead.web_url || ''}
              placeholder="https://..."
              mostrado={lead.web_url
                ? <a className="ld-enlace" href={lead.web_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                    <IconGlobe size={11} /> {urlCorta(lead.web_url)}
                  </a>
                : <span className="text-muted-italic">Sin sitio</span>}
            />
          </div>

          <div className="ld-senal">
            <span className="ld-dato__label">Señal de dolor</span>
            {editando === 'senal_dolor' ? (
              <>
                <textarea
                  className="form-input ld-senal__editor"
                  value={senalDraft}
                  onChange={e => setSenalDraft(e.target.value)}
                  placeholder="Qué se vio en su web, Instagram o LinkedIn que indica necesidad..."
                  autoFocus
                />
                <div className="ld-senal__acciones">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditando(null)}>Cancelar</button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => guardar('senal_dolor', senalDraft.trim() || null)}
                  >
                    Guardar
                  </button>
                </div>
              </>
            ) : (
              <button
                type="button"
                className="ld-senal__texto"
                onClick={() => { setSenalDraft(lead.senal_dolor || ''); setEditando('senal_dolor') }}
              >
                {lead.senal_dolor || <span className="text-muted-italic">Sin señal registrada</span>}
                <IconEdit size={11} />
              </button>
            )}
          </div>
        </section>
      )}

      {/* ─── Formulario ─── */}
      {formData.length > 0 && (
        <section className="ld-bloque">
          <h4 className="ld-bloque__titulo">Respuestas del formulario</h4>
          <div className="ld-form-responses">
            {formData.map((item, idx) => (
              <div key={idx} className="ld-form-item">
                <span className="ld-form-item__q">{item.pregunta}</span>
                <span className="ld-form-item__a">
                  {Array.isArray(item.respuesta) ? item.respuesta.join(', ') : item.respuesta}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ─── UTM ─── */}
      {(lead.utm_source || lead.utm_medium || lead.utm_campaign || lead.referrer_url) && (
        <details className="ld-bloque ld-utm-details">
          <summary className="ld-bloque__titulo">Atribución / UTM</summary>
          <div className="ld-utm-data">
            {lead.utm_source && <div className="ld-utm-row"><strong>Source:</strong> {lead.utm_source}</div>}
            {lead.utm_medium && <div className="ld-utm-row"><strong>Medium:</strong> {lead.utm_medium}</div>}
            {lead.utm_campaign && <div className="ld-utm-row"><strong>Campaign:</strong> {lead.utm_campaign}</div>}
            {lead.referrer_url && <div className="ld-utm-row"><strong>Referrer:</strong> {lead.referrer_url}</div>}
          </div>
        </details>
      )}

    </div>
  )
}
